// src/components/HamburgerMenu.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaBars,
  FaCross,
  FaHome,
  FaPlayCircle,
  FaMusic,
  FaVideo,
  FaCalendarAlt,
  FaBookReader,
  FaChalkboardTeacher,
  FaChartPie,
  FaPrayingHands,
  FaListUl,
  FaTv,
  FaUsers,
  FaQuestionCircle,
  FaWaveSquare,
  FaHeadphones,
  FaThumbtack,
  FaHandsHelping,
  FaComments,
  FaPray,
  FaUserCircle,
  FaUserTie,
  FaUserMd,
  FaShareAlt,
  FaMedal,
  FaStore,
  FaBlog,
  FaPodcast,
  FaBook,
  FaShoppingCart,
  FaStickyNote,
} from "react-icons/fa";
import "./HamburgerMenu.css"; // Import specific styles

const BETA_PAGES = [
  { label: "Playlists", to: "/playlists", Icon: FaListUl },
  { label: "Resources", to: "/resources", Icon: FaBook },
  { label: "Bible Study", to: "/biblestudy", Icon: FaBookReader },
  { label: "Community", to: "/community", Icon: FaUsers },
  { label: "Volunteer", to: "/volunteer", Icon: FaHandsHelping },
  { label: "Forums", to: "/forums", Icon: FaComments },
  { label: "Prayer Wall", to: "/prayerwall", Icon: FaPray },
  { label: "Profile", to: "/profile", Icon: FaUserCircle },
  { label: "Podcasts", to: "/podcasts", Icon: FaPodcast },
  { label: "Blog", to: "/blog", Icon: FaBlog },
  { label: "Marketplace", to: "/marketplace", Icon: FaStore },
  { label: "Gamification", to: "/gamification", Icon: FaMedal },
  { label: "Social Media", to: "/socialmedia", Icon: FaShareAlt },
  { label: "Counseling", to: "/counseling", Icon: FaUserMd },
  { label: "Mentorship", to: "/mentorship", Icon: FaUserTie },
  { label: "Media Tools", to: "/media-tools", Icon: FaVideo },
];

function HamburgerMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const ignoreClickRef = useRef(false);
  const location = useLocation();
  const betaParam = useMemo(
    () => new URLSearchParams(location.search).get("beta") || "",
    [location.search]
  );
  const betaEnabled = ["on", "true", "1"].includes(betaParam.toLowerCase());
  const currentSearch = location.search;
  const navOpen = pinned || isOpen;

  useEffect(() => {
    if (pinned) {
      setIsOpen(true);
    }
  }, [pinned]);

  useEffect(() => {
    const className = "nav-pinned-body";
    if (pinned) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    return () => document.body.classList.remove(className);
  }, [pinned]);

  const toggleMenu = () => {
    if (pinned) {
      setPinned(false);
    }
    setIsOpen((prev) => !prev);
  };
  const openMenu = () => setIsOpen(true);
  const closeMenu = () => {
    if (pinned) return;
    setIsOpen(false);
  };
  const closeOnClick = () => {
    if (!pinned) {
      setIsOpen(false);
    }
  };
  const handlePointerDown = (event) => {
    if (event.pointerType === "touch") {
      ignoreClickRef.current = true;
      toggleMenu();
    }
  };
  const handleClick = () => {
    if (ignoreClickRef.current) {
      ignoreClickRef.current = false;
      return;
    }
    toggleMenu();
  };

  return (
    <>
      <div className="hamburger-menu">
        <button
          className="menu-button"
          onMouseEnter={openMenu}
          onPointerDown={handlePointerDown}
          onClick={handleClick}
          aria-label="Toggle Menu"
        >
          {navOpen ? <FaCross /> : <FaBars />}
        </button>
      </div>
      <nav
        className={`nav-links ${navOpen ? "open" : ""} ${pinned ? "pinned" : ""}`}
        onMouseEnter={openMenu}
        onMouseLeave={closeMenu}
        aria-label="Main navigation"
      >
        <Link to="/" onClick={closeOnClick} aria-label="Home">
          <FaHome className="nav-icon" />
          <span className="nav-text">Home</span>
        </Link>
        <Link to="/worship" onClick={closeOnClick} aria-label="Worship">
          <FaPrayingHands className="nav-icon" />
          <span className="nav-text">Worship</span>
        </Link>
        <Link to="/bibleis" onClick={closeOnClick} aria-label="Bible.is Reader">
          <FaBookReader className="nav-icon" />
          <span className="nav-text">Bible</span>
        </Link>
        <Link to="/video" onClick={closeOnClick} aria-label="Video Explorer">
          <FaPlayCircle className="nav-icon" />
          <span className="nav-text">Videos</span>
        </Link>
        <Link to="/audio" onClick={closeOnClick} aria-label="Audio Library">
          <FaHeadphones className="nav-icon" />
          <span className="nav-text">Audio</span>
        </Link>
        {betaEnabled && (
          <Link to={{ pathname: "/notes", search: currentSearch }} onClick={closeOnClick} aria-label="Notes">
            <FaStickyNote className="nav-icon" />
            <span className="nav-text">Notes</span>
          </Link>
        )}
        <Link to="/present" onClick={closeOnClick} aria-label="Present">
          <FaChartPie className="nav-icon" />
          <span className="nav-text">Present</span>
        </Link>
        <Link to="/session" onClick={closeOnClick} aria-label="Session">
          <FaListUl className="nav-icon" />
          <span className="nav-text">Session</span>
        </Link>
        <Link to="/dj" onClick={closeOnClick} aria-label="DJ Mixer">
          <FaWaveSquare className="nav-icon" />
          <span className="nav-text">DJ Mixer</span>
        </Link>
        <Link to="/music" onClick={closeOnClick} aria-label="Music">
          <FaMusic className="nav-icon" />
          <span className="nav-text">Music</span>
        </Link>
        <Link to="/live" onClick={closeOnClick} aria-label="GCC TV">
          <FaTv className="nav-icon" />
          <span className="nav-text">GCC TV</span>
        </Link>
        <a
          href="https://www.youtube.com/@gcc-worship?sub_confirmation=1"
          onClick={closeOnClick}
          className="nav-icon-link"
          aria-label="Join GCC"
        >
          <FaUsers className="nav-icon" />
          <span className="nav-text">Join</span>
        </a>
        <Link to="/events" onClick={closeOnClick} aria-label="Events">
          <FaCalendarAlt className="nav-icon" />
          <span className="nav-text">Events</span>
        </Link>
        <Link to="/store" onClick={closeOnClick} aria-label="Store">
          <FaShoppingCart className="nav-icon" />
          <span className="nav-text">Store</span>
        </Link>
        <Link to="/songs" onClick={closeOnClick} aria-label="Songs">
          <FaVideo className="nav-icon" />
          <span className="nav-text">Songs</span>
        </Link>
        <Link to="/karaoke" onClick={closeOnClick} aria-label="Karaoke">
          <FaMusic className="nav-icon" />
          <span className="nav-text">Karaoke</span>
        </Link>
        <Link to="/messages" onClick={closeOnClick} aria-label="Messages">
          <FaChalkboardTeacher className="nav-icon" />
          <span className="nav-text">Messages</span>
        </Link>
        <Link to="/help" onClick={closeOnClick} aria-label="Help">
          <FaQuestionCircle className="nav-icon" />
          <span className="nav-text">Help</span>
        </Link>
        <button
          type="button"
          className={`nav-panel-pin nav-control ${pinned ? "active" : ""}`}
          onClick={() => setPinned((prev) => !prev)}
          aria-pressed={pinned}
          aria-label={pinned ? "Unpin navigation" : "Pin navigation"}
        >
          <FaThumbtack className="nav-icon" aria-hidden="true" />
          <span className="nav-text">{pinned ? "Unpin navigation" : "Pin navigation"}</span>
        </button>
        {betaEnabled && (
          <div className="beta-section">
            <div id="beta-links" className="beta-links">
              {BETA_PAGES.map(({ label, to, Icon }) => (
                <Link
                  key={label}
                  to={{ pathname: to, search: currentSearch }}
                  onClick={closeOnClick}
                  aria-label={label}
                >
                  <Icon className="nav-icon" />
                  <span className="nav-text">{label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

export default HamburgerMenu;
